import {
  hrSettingsAdminPatterns,
  hrSettingsAdminTranslations,
} from './translations/hrSettingsAdmin';
import {
  inventoryAccountingPatterns,
  inventoryAccountingTranslations,
} from './translations/inventoryAccounting';
import {
  residualPatterns,
  residualTranslations,
} from './translations/residual';
import {
  reportsPosPublicPatterns,
  reportsPosPublicTranslations,
} from './translations/reportsPosPublic';
import {
  salesPurchasesPatterns,
  salesPurchasesTranslations,
} from './translations/salesPurchases';

export type AppLanguage = 'en' | 'ar';

export const APP_LANGUAGE_STORAGE_KEY = 'app_language';

export const LANGUAGE_CHANGE_EVENT = 'accountix-language-change';

export const notifyLanguageChange = () => {
  if (typeof window !== 'undefined') window.dispatchEvent(new Event(LANGUAGE_CHANGE_EVENT));
};

export const normalizeLanguage = (value: unknown): AppLanguage => value === 'ar' ? 'ar' : 'en';

export const getStoredLanguage = (): AppLanguage => {
  if (typeof window === 'undefined') return 'en';

  const stored = window.localStorage.getItem(APP_LANGUAGE_STORAGE_KEY);
  if (stored === 'ar' || stored === 'en') return stored;

  try {
    const legacyUser = JSON.parse(window.localStorage.getItem('user') || 'null');
    if (legacyUser?.language === 'ar' || legacyUser?.language === 'en') return legacyUser.language;
  } catch {
    // Ignore invalid legacy profile data.
  }

  return window.navigator.language.toLowerCase().startsWith('ar') ? 'ar' : 'en';
};

export const getLocale = (language: AppLanguage = getStoredLanguage()): string =>
  language === 'ar' ? 'ar-EG-u-nu-latn' : 'en-US';

const ARABIC_EXACT_TRANSLATIONS: Record<string, string> = {
  ...salesPurchasesTranslations,
  ...inventoryAccountingTranslations,
  ...hrSettingsAdminTranslations,
  ...reportsPosPublicTranslations,
  ...residualTranslations,
  'Dashboard': 'لوحة القيادة',
  'Sales': 'المبيعات',
  'Quotes': 'عروض الأسعار',
  'Invoices': 'الفواتير',
  'Deliveries': 'التسليمات',
  'POS': 'نقطة البيع',
  'Sell Screen': 'شاشة البيع',
  'Sales History': 'سجل المبيعات',
  'Catalog': 'الكتالوج',
  'Stores': 'المتاجر',
  'Purchases': 'المشتريات',
  'Purchase Orders': 'أوامر الشراء',
  'Purchase Order': 'أمر شراء',
  'Bills': 'فواتير المشتريات',
  'Expense Submissions': 'طلبات المصروفات',
  'Landed Costs': 'التكاليف الإضافية',
  'Contacts': 'جهات الاتصال',
  'All Contacts': 'جميع جهات الاتصال',
  'Customers': 'العملاء',
  'Suppliers': 'الموردون',
  'Inventory': 'المخزون',
  'Inventory Manager': 'إدارة المخزون',
  'Inventory Reports': 'تقارير المخزون',
  'HR': 'الموارد البشرية',
  'PayRun': 'تشغيل الرواتب',
  'Employees': 'الموظفون',
  'Payrun Settings': 'إعدادات الرواتب',
  'Check In and Check Out': 'تسجيل الحضور والانصراف',
  'Task/Activities': 'المهام والأنشطة',
  'Discussions': 'المناقشات',
  'Reports': 'التقارير',
  'Accounting': 'المحاسبة',
  'Manual Journal': 'قيد يومية يدوي',
  'Trial Balance': 'ميزان المراجعة',
  'Configuration': 'الإعدادات المحاسبية',
  'Settings': 'الإعدادات',
  'Help & How To': 'المساعدة ودليل الاستخدام',
  'Activity Log': 'سجل النشاط',
  'Super Admin': 'المشرف العام',
  'Super Admin Panel': 'لوحة تحكم المشرف العام',
  'Workspace': 'مساحة العمل',
  'Finance workspace': 'مساحة العمل المالية',
  'Organization': 'المؤسسة',
  'Modules': 'الوحدات',
  'Create': 'إنشاء',
  'Invoice': 'فاتورة مبيعات',
  'Bill': 'فاتورة مشتريات',
  'Quote': 'عرض سعر',
  'Contact': 'جهة اتصال',
  'Journal entry': 'قيد يومية',
  'Notifications': 'الإشعارات',
  'No new notifications': 'لا توجد إشعارات جديدة',
  'New notification': 'إشعار جديد',
  'Profile': 'الملف الشخصي',
  'Log out': 'تسجيل الخروج',
  'Sign Out': 'تسجيل الخروج',
  'Search': 'بحث',
  'Search transactions': 'البحث في المعاملات',
  'Search transactions...': 'ابحث في المعاملات...',
  'Save': 'حفظ',
  'Save Changes': 'حفظ التغييرات',
  'Saving...': 'جاري الحفظ...',
  'Cancel': 'إلغاء',
  'Close': 'إغلاق',
  'Delete': 'حذف',
  'Edit': 'تعديل',
  'Update': 'تحديث',
  'Add': 'إضافة',
  'New': 'جديد',
  'Create New': 'إنشاء جديد',
  'Submit': 'إرسال',
  'Approve': 'اعتماد',
  'Reject': 'رفض',
  'Reset': 'إعادة ضبط',
  'Reset Filters': 'إعادة ضبط عوامل التصفية',
  'Filters': 'عوامل التصفية',
  'All': 'الكل',
  'None': 'لا شيء',
  'Yes': 'نعم',
  'No': 'لا',
  'Name': 'الاسم',
  'Description': 'الوصف',
  'Status': 'الحالة',
  'Date': 'التاريخ',
  'Due Date': 'تاريخ الاستحقاق',
  'Reference': 'المرجع',
  'Number': 'الرقم',
  'Amount': 'المبلغ',
  'Total': 'الإجمالي',
  'Subtotal': 'الإجمالي الفرعي',
  'Tax': 'الضريبة',
  'Currency': 'العملة',
  'Customer': 'العميل',
  'Supplier': 'المورد',
  'Vendor': 'المورد',
  'Account': 'الحساب',
  'Accounts': 'الحسابات',
  'Category': 'الفئة',
  'Tags': 'الوسوم',
  'All Tags': 'جميع الوسوم',
  'Payment Status': 'حالة الدفع',
  'Payment Method': 'طريقة الدفع',
  'Paid': 'مدفوع',
  'Unpaid': 'غير مدفوع',
  'Partially Paid': 'مدفوع جزئيًا',
  'Draft': 'مسودة',
  'Pending': 'قيد الانتظار',
  'Approved': 'معتمد',
  'Rejected': 'مرفوض',
  'Completed': 'مكتمل',
  'Active': 'نشط',
  'Inactive': 'غير نشط',
  'Overdue': 'متأخر',
  'Open': 'مفتوح',
  'Closed': 'مغلق',
  'Loading...': 'جاري التحميل...',
  'No data found': 'لا توجد بيانات',
  'No results found': 'لم يتم العثور على نتائج',
  'No transactions found.': 'لا توجد معاملات.',
  'All Payment Status': 'جميع حالات الدفع',
  'All Statuses': 'جميع الحالات',
  'All Categories': 'جميع الفئات',
  'All Accounts': 'جميع الحسابات',
  'Select an account': 'اختر حسابًا',
  'Select a customer': 'اختر عميلًا',
  'Select a supplier': 'اختر موردًا',
  'Select a vendor': 'اختر موردًا',
  'Select Currency': 'اختر العملة',
  'Select Theme': 'اختر المظهر',
  'Min': 'الحد الأدنى',
  'Max': 'الحد الأقصى',
  'Professional': 'احترافي',
  'Minimal': 'بسيط',
  'Standard': 'قياسي',
  'Modern': 'عصري',
  'Both': 'كلاهما',
  'Tax Exempt (0%)': 'معفى من الضريبة (0٪)',
  'No narration provided': 'لم يتم إدخال وصف للقيد',
  'Search journals...': 'ابحث في القيود...',
  'Not provided': 'غير متوفر',
  'Tax ID / Number': 'الرقم الضريبي',
  'Trade License Number': 'رقم الرخصة التجارية',
  'Add New Line': 'إضافة بند جديد',
  'Line Items': 'بنود المستند',
  'Select a tag...': 'اختر وسمًا...',
  'Select organization': 'اختر المؤسسة',
  'Local development organization': 'مؤسسة التطوير المحلية',
  'Current': 'حالي',
  'Actual': 'الفعلي',
  'Budget': 'الموازنة',
  'Variance': 'الانحراف',
  'Total Assets': 'إجمالي الأصول',
  'Total Liabilities': 'إجمالي الالتزامات',
  'Total Equity': 'إجمالي حقوق الملكية',
  'Total Outstanding': 'إجمالي المستحق',
  'Revenue': 'الإيرادات',
  'Expenses': 'المصروفات',
  'Cash Flow': 'التدفق النقدي',
  'This Month': 'هذا الشهر',
  'Pending Invoices': 'الفواتير المعلقة',
  'Awaiting customer payment': 'بانتظار سداد العميل',
  'User Profile': 'الملف الشخصي',
  'Personal Information': 'المعلومات الشخصية',
  'Full Name': 'الاسم الكامل',
  'Email Address': 'البريد الإلكتروني',
  'Language Preferences': 'تفضيلات اللغة',
  'Display Language': 'لغة العرض',
  'Theme & Layout Preferences': 'تفضيلات المظهر والتخطيط',
  'Profile updated successfully': 'تم تحديث الملف الشخصي بنجاح',
  'Failed to update profile': 'تعذر تحديث الملف الشخصي',
  'Failed to load profile data': 'تعذر تحميل بيانات الملف الشخصي',
  'Analyzing your accounting system screenshot...': 'جاري تحليل لقطة شاشة النظام المحاسبي...',
  'Gemini is extracting features and layout details': 'يجري استخراج الميزات وتفاصيل التخطيط',
  'AI Analysis Results': 'نتائج تحليل الذكاء الاصطناعي',
  'Apply AI Recommendations': 'تطبيق توصيات الذكاء الاصطناعي',
};

const ARABIC_PATTERNS: Array<[RegExp, (...matches: string[]) => string]> = [
  ...salesPurchasesPatterns,
  ...inventoryAccountingPatterns,
  ...hrSettingsAdminPatterns,
  ...reportsPosPublicPatterns,
  ...residualPatterns,
  [/^(\d+) unread$/, count => `${count} غير مقروءة`],
  [/^Current organization:\s*(.+)$/i, organization => `المؤسسة الحالية: ${organization}`],
  [/^As of\s+(.+)$/i, date => `حتى ${date}`],
  [/^As at\s+(.+)$/i, date => `كما في ${date}`],
  [/^For the month ended\s+(.+)$/i, date => `للشهر المنتهي في ${date}`],
  [/^For the year ended\s+(.+)$/i, date => `للسنة المنتهية في ${date}`],
  [/^For the period\s+(.+)\s+to\s+(.+)$/i, (start, end) => `للفترة من ${start} إلى ${end}`],
  [/^(\d+) Days$/i, days => `${days} يومًا`],
];

const ARABIC_DATE_TOKENS: Record<string, string> = {
  January: 'يناير', February: 'فبراير', March: 'مارس', April: 'أبريل', May: 'مايو', June: 'يونيو',
  July: 'يوليو', August: 'أغسطس', September: 'سبتمبر', October: 'أكتوبر', November: 'نوفمبر', December: 'ديسمبر',
  Jan: 'يناير', Feb: 'فبراير', Mar: 'مارس', Apr: 'أبريل', Jun: 'يونيو', Jul: 'يوليو', Aug: 'أغسطس',
  Sep: 'سبتمبر', Sept: 'سبتمبر', Oct: 'أكتوبر', Nov: 'نوفمبر', Dec: 'ديسمبر',
  Sunday: 'الأحد', Monday: 'الاثنين', Tuesday: 'الثلاثاء', Wednesday: 'الأربعاء', Thursday: 'الخميس', Friday: 'الجمعة', Saturday: 'السبت',
  Sun: 'الأحد', Mon: 'الاثنين', Tue: 'الثلاثاء', Wed: 'الأربعاء', Thu: 'الخميس', Fri: 'الجمعة', Sat: 'السبت',
};

export const translateArabicText = (value: string): string => {
  const leading = value.match(/^\s*/)?.[0] || '';
  const trailing = value.match(/\s*$/)?.[0] || '';
  const text = value.trim();
  if (!text) return value;

  const exact = ARABIC_EXACT_TRANSLATIONS[text];
  if (exact) return `${leading}${exact}${trailing}`;

  for (const [pattern, replacement] of ARABIC_PATTERNS) {
    const match = text.match(pattern);
    if (match) return `${leading}${replacement(...match.slice(1))}${trailing}`;
  }

  const dateLocalized = Object.entries(ARABIC_DATE_TOKENS).reduce(
    (result, [token, translation]) => result.replace(new RegExp(`\\b${token}\\b`, 'g'), translation),
    text,
  );
  if (dateLocalized !== text) return `${leading}${dateLocalized}${trailing}`;

  return value;
};

const LOCALIZABLE_ATTRIBUTES = ['placeholder', 'title', 'aria-label', 'aria-description', 'alt'] as const;
const SKIPPED_TAGS = new Set(['SCRIPT', 'STYLE', 'CODE', 'PRE', 'TEXTAREA']);

const localizeTextNode = (node: Text, originalText: Map<Text, string>) => {
  if (!node.parentElement || SKIPPED_TAGS.has(node.parentElement.tagName)) return;
  const currentValue = node.nodeValue || '';
  const translated = translateArabicText(currentValue);
  if (translated === currentValue) return;
  if (!originalText.has(node)) originalText.set(node, currentValue);
  node.nodeValue = translated;
};

export type LocalizableFormControl = HTMLInputElement | HTMLTextAreaElement;

const localizeElement = (
  element: Element,
  originalAttributes: Map<Element, Map<string, string>>,
  originalInputValues: Map<LocalizableFormControl, string>,
) => {
  if (element instanceof HTMLInputElement && ['button', 'submit', 'reset'].includes(element.type)) {
    const translated = translateArabicText(element.value);
    if (translated !== element.value) {
      if (!originalInputValues.has(element)) originalInputValues.set(element, element.value);
      element.value = translated;
    }
  }

  if (element instanceof HTMLTextAreaElement) {
    const translated = translateArabicText(element.value);
    if (translated !== element.value) {
      if (!originalInputValues.has(element)) originalInputValues.set(element, element.value);
      element.value = translated;
    }
  }

  for (const attribute of LOCALIZABLE_ATTRIBUTES) {
    const value = element.getAttribute(attribute);
    if (!value) continue;
    const translated = translateArabicText(value);
    if (translated === value) continue;
    let attributes = originalAttributes.get(element);
    if (!attributes) {
      attributes = new Map<string, string>();
      originalAttributes.set(element, attributes);
    }
    if (!attributes.has(attribute)) attributes.set(attribute, value);
    element.setAttribute(attribute, translated);
  }
};

const localizeTree = (
  root: Node,
  originalText: Map<Text, string>,
  originalAttributes: Map<Element, Map<string, string>>,
  originalInputValues: Map<LocalizableFormControl, string>,
) => {
  if (root.nodeType === Node.TEXT_NODE) {
    localizeTextNode(root as Text, originalText);
    return;
  }

  if (!(root instanceof Element) && !(root instanceof DocumentFragment)) return;
  if (root instanceof Element) localizeElement(root, originalAttributes, originalInputValues);

  const walker = document.createTreeWalker(root, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT);
  let current = walker.nextNode();
  while (current) {
    if (current.nodeType === Node.TEXT_NODE) localizeTextNode(current as Text, originalText);
    else localizeElement(current as Element, originalAttributes, originalInputValues);
    current = walker.nextNode();
  }
};

export const localizePrintableHtml = (html: string): string => {
  if (getStoredLanguage() !== 'ar' || typeof DOMParser === 'undefined') return html;
  const printableDocument = new DOMParser().parseFromString(html, 'text/html');
  printableDocument.documentElement.lang = 'ar';
  printableDocument.documentElement.dir = 'rtl';
  printableDocument.title = translateArabicText(printableDocument.title);

  const walker = printableDocument.createTreeWalker(printableDocument.documentElement, NodeFilter.SHOW_ELEMENT | NodeFilter.SHOW_TEXT);
  let current: Node | null = walker.currentNode;
  while (current) {
    if (current.nodeType === Node.TEXT_NODE && current.parentElement && !SKIPPED_TAGS.has(current.parentElement.tagName)) {
      current.nodeValue = translateArabicText(current.nodeValue || '');
    } else if (current instanceof Element) {
      for (const attribute of LOCALIZABLE_ATTRIBUTES) {
        const value = current.getAttribute(attribute);
        if (value) current.setAttribute(attribute, translateArabicText(value));
      }
    }
    current = walker.nextNode();
  }

  const style = printableDocument.createElement('style');
  style.textContent = 'html[dir="rtl"] body { direction: rtl; font-family: "Noto Sans Arabic", Tahoma, Arial, sans-serif; }';
  printableDocument.head.appendChild(style);
  return `<!DOCTYPE html>\n${printableDocument.documentElement.outerHTML}`;
};

export const localizePdfText = (value: unknown): string => getStoredLanguage() === 'ar'
  ? translateArabicText(String(value ?? ''))
  : String(value ?? '');

const ARABIC_PDF_TEXT = /[\u0600-\u06ff\u0750-\u077f\u08a0-\u08ff\ufb50-\ufdff\ufe70-\ufeff]/;

export const localizeJsPdfInstance = <T extends { text: (...args: any[]) => any }>(pdf: T): T => {
  const nativeText = pdf.text.bind(pdf);
  const pdfWithFonts = pdf as T & {
    getFont?: () => { fontStyle?: string };
    setFont?: (fontName: string, fontStyle?: string) => unknown;
  };

  pdf.text = ((text: unknown, ...args: any[]) => {
    const localizedText = getStoredLanguage() === 'ar'
      ? (Array.isArray(text) ? text.map(localizePdfText) : localizePdfText(text))
      : text;
    const textContent = Array.isArray(localizedText) ? localizedText.join('\n') : String(localizedText ?? '');
    const requestedStyle = pdfWithFonts.getFont?.().fontStyle || 'normal';

    if (ARABIC_PDF_TEXT.test(textContent)) {
      // Only the regular Arabic font is bundled. Registering that same file as
      // bold produces blank glyphs in jsPDF, so Arabic text must use normal.
      pdfWithFonts.setFont?.('NotoSansArabic', 'normal');
    } else {
      pdfWithFonts.setFont?.('helvetica', requestedStyle);
    }

    return nativeText(localizedText, ...args);
  }) as T['text'];
  return pdf;
};

export const localizeNativeDialogs = (): (() => void) => {
  if (typeof window === 'undefined') return () => undefined;

  const nativeAlert = window.alert.bind(window);
  const nativeConfirm = window.confirm.bind(window);
  const nativePrompt = window.prompt.bind(window);
  const localize = (message?: string) => getStoredLanguage() === 'ar'
    ? translateArabicText(String(message ?? ''))
    : String(message ?? '');

  window.alert = message => nativeAlert(localize(message));
  window.confirm = message => nativeConfirm(localize(message));
  window.prompt = (message, defaultValue) => nativePrompt(localize(message), defaultValue);

  return () => {
    window.alert = nativeAlert;
    window.confirm = nativeConfirm;
    window.prompt = nativePrompt;
  };
};

export const startGlobalArabicLocalization = (): (() => void) => {
  if (typeof window === 'undefined' || typeof document === 'undefined') return () => undefined;
  let stopDomLocalization: (() => void) | undefined;

  const apply = () => {
    stopDomLocalization?.();
    stopDomLocalization = undefined;
    const language = getStoredLanguage();
    document.documentElement.lang = language;
    document.documentElement.dir = language === 'ar' ? 'rtl' : 'ltr';
    document.body.dir = language === 'ar' ? 'rtl' : 'ltr';
    if (language === 'ar') stopDomLocalization = startArabicDomLocalization(document.body);
  };

  apply();
  window.addEventListener(LANGUAGE_CHANGE_EVENT, apply);
  window.addEventListener('storage', apply);

  return () => {
    stopDomLocalization?.();
    window.removeEventListener(LANGUAGE_CHANGE_EVENT, apply);
    window.removeEventListener('storage', apply);
  };
};

export const startArabicDomLocalization = (root: HTMLElement): (() => void) => {
  let frame = 0;
  const pendingNodes = new Set<Node>();
  const originalText = new Map<Text, string>();
  const originalAttributes = new Map<Element, Map<string, string>>();
  const originalInputValues = new Map<LocalizableFormControl, string>();

  const flush = () => {
    frame = 0;
    pendingNodes.forEach(node => localizeTree(node, originalText, originalAttributes, originalInputValues));
    pendingNodes.clear();
  };

  const schedule = (node: Node) => {
    pendingNodes.add(node);
    if (!frame) frame = window.requestAnimationFrame(flush);
  };

  localizeTree(root, originalText, originalAttributes, originalInputValues);
  const observer = new MutationObserver(mutations => {
    for (const mutation of mutations) {
      if (mutation.type === 'characterData' || mutation.type === 'attributes') schedule(mutation.target);
      mutation.addedNodes.forEach(schedule);
    }
  });

  observer.observe(root, {
    subtree: true,
    childList: true,
    characterData: true,
    attributes: true,
    attributeFilter: [...LOCALIZABLE_ATTRIBUTES],
  });

  const timer = window.setInterval(() => {
    if (getStoredLanguage() !== 'ar') return;
    localizeTree(document.body, originalText, originalAttributes, originalInputValues);
  }, 750);

  return () => {
    observer.disconnect();
    window.clearInterval(timer);
    if (frame) window.cancelAnimationFrame(frame);
    originalText.forEach((value, node) => {
      if (node.isConnected) node.nodeValue = value;
    });
    originalAttributes.forEach((attributes, element) => {
      if (!element.isConnected) return;
      attributes.forEach((value, attribute) => element.setAttribute(attribute, value));
    });
    originalInputValues.forEach((value, input) => {
      if (input.isConnected) input.value = value;
    });
  };
};
